import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Building2, Users, CalendarDays, Plus, Trash2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { SiteHeader, SiteFooter } from "@/pages/site/SiteLayout";
import { useSiteContent } from "@/pages/site/siteContent";

const API = process.env.REACT_APP_BACKEND_URL;
const inputCls = "w-full h-11 rounded-lg border border-slate-600 px-3 text-sm text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#00D2C2] bg-[#1A232E]";
const labelCls = "text-xs font-semibold text-slate-300 block mb-1";
const YESNO = ["", "Yes", "No"];

const EMPTY_CONTACT = { name: "", role: "", email: "", phone: "" };
const EMPTY_EVENT = {
  date_of_service: "", service_location: "", start_time: "", end_time: "",
  participants: "", sites: "", overnight: "", max_capacity: "",
  alcohol: "", security: "", parking: "", freshwater: "", power: "", additional_info: "",
};

export default function PublicIntake() {
  const [params] = useSearchParams();
  const site = useSiteContent();
  const kitSlug = params.get("kit");
  const kitSection = kitSlug
    ? (site.page_sections?.offers || []).find((s) => s.type === "kit_detail" && (s.slug || "") === kitSlug)
    : null;
  const [kitChecks, setKitChecks] = useState({});
  const [org, setOrg] = useState({ company_name: "", email: "", phone: "", address: "" });
  const [contacts, setContacts] = useState([{ ...EMPTY_CONTACT }]);
  const [ev, setEv] = useState({ ...EMPTY_EVENT });
  const [wantCover, setWantCover] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const setOrgField = (k) => (e) => setOrg((f) => ({ ...f, [k]: e.target.value }));
  const setEvField = (k) => (e) => setEv((f) => ({ ...f, [k]: e.target.value }));
  const setContact = (i, k, v) => setContacts((cs) => cs.map((c, j) => (j === i ? { ...c, [k]: v } : c)));

  const yesNo = (k, label, testid) => (
    <label className="block" key={k}>
      <span className={labelCls}>{label}</span>
      <select value={ev[k]} onChange={setEvField(k)} data-testid={testid} className={inputCls}>
        {YESNO.map((o) => <option key={o} value={o}>{o || "Select…"}</option>)}
      </select>
    </label>
  );

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const eventPayload = (wantCover || kitSection) ? {
        ...ev,
        ...(kitSection ? {
          kit: kitSection.heading || kitSlug,
          kit_requirements: (kitSection.requests || []).filter(Boolean).map((item, i) => ({ item, confirmed: !!kitChecks[i] })),
        } : {}),
      } : null;
      const r = await fetch(`${API}/api/public/client-intake`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...org, contacts, event: eventPayload }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "Couldn't send — please try again");
      setDone(true);
      setTimeout(() => { window.location.href = "https://ukmedicalevents.co.uk"; }, 3000);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#0B0F14]" data-testid="intake-success">
        <SiteHeader />
        <div className="flex items-center justify-center p-4 py-20">
        <div className="bg-[#121820] rounded-2xl border border-slate-700 p-8 max-w-md text-center shadow-sm">
          <CheckCircle2 className="h-12 w-12 text-[#00D2C2] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white">Thank you — details received</h1>
          <p className="text-sm text-slate-400 mt-2">
            Wales Event Medical Services has your details and will be in touch shortly
            {wantCover ? " with a quote for your event" : ""}.
          </p>
          <p className="text-xs text-slate-400 mt-4" data-testid="intake-redirect-note">
            Taking you to ukmedicalevents.co.uk…
          </p>
        </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0F14]" data-testid="public-intake">
      <SiteHeader />
      <div className="bg-[#0F2B32] border-b border-[#00D2C2]/25">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <p className="font-bold text-white text-sm">Event Medical Cover — Your Details</p>
          <p className="text-xs text-slate-400">Fill this in and we can upload you onto the system.</p>
        </div>
      </div>

      <form onSubmit={submit} className="max-w-2xl mx-auto p-4 sm:p-6 space-y-5">
        {kitSection && (
          <section className="bg-[#0F2B32] rounded-2xl border border-[#00D2C2]/30 p-5" data-testid="intake-kit">
            <p className="text-xs font-bold uppercase tracking-widest text-[#00D2C2]">You're quoting for</p>
            <h2 className="font-bold text-white text-lg mt-1">{kitSection.heading}</h2>
            {(kitSection.requests || []).filter(Boolean).length > 0 && (
              <div className="mt-3 space-y-2.5">
                <p className="text-xs text-slate-300">Please confirm what you're able to supply:</p>
                {kitSection.requests.filter(Boolean).map((r, i) => (
                  <label key={i} className="flex items-start gap-2.5 text-sm text-slate-200 cursor-pointer" data-testid={`intake-kit-req-${i}`}>
                    <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#00D2C2]" checked={!!kitChecks[i]}
                      onChange={(e) => setKitChecks((c) => ({ ...c, [i]: e.target.checked }))} />
                    {r}
                  </label>
                ))}
              </div>
            )}
          </section>
        )}
        <section className="bg-[#121820] rounded-2xl border border-slate-700 p-5 space-y-3" data-testid="intake-org">
          <h2 className="font-bold text-white flex items-center gap-2"><Building2 className="h-4 w-4 text-[#00D2C2]" /> Your Organisation</h2>
          <label className="block">
            <span className={labelCls}>Club / business name *</span>
            <input required value={org.company_name} onChange={setOrgField("company_name")} data-testid="intake-company" className={inputCls} />
          </label>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className={labelCls}>Main email</span>
              <input type="email" value={org.email} onChange={setOrgField("email")} data-testid="intake-email" className={inputCls} />
            </label>
            <label className="block">
              <span className={labelCls}>Main phone</span>
              <input value={org.phone} onChange={setOrgField("phone")} data-testid="intake-phone" className={inputCls} />
            </label>
          </div>
          <label className="block">
            <span className={labelCls}>Address</span>
            <input value={org.address} onChange={setOrgField("address")} data-testid="intake-address" className={inputCls} />
          </label>
        </section>

        <section className="bg-[#121820] rounded-2xl border border-slate-700 p-5 space-y-3" data-testid="intake-people">
          <h2 className="font-bold text-white flex items-center gap-2"><Users className="h-4 w-4 text-[#00D2C2]" /> People We Should Know</h2>
          <p className="text-xs text-slate-400">Whoever we might need to speak to — organiser, secretary, treasurer, safety officer…</p>
          {contacts.map((c, i) => (
            <div key={i} className="rounded-xl border border-slate-700 p-3 grid sm:grid-cols-2 gap-2 relative" data-testid={`intake-contact-${i}`}>
              <input value={c.name} onChange={(e) => setContact(i, "name", e.target.value)} placeholder="Name"
                data-testid={`intake-contact-name-${i}`} className={inputCls} />
              <input value={c.role} onChange={(e) => setContact(i, "role", e.target.value)} placeholder="Role (e.g. Organiser)"
                data-testid={`intake-contact-role-${i}`} className={inputCls} />
              <input type="email" value={c.email} onChange={(e) => setContact(i, "email", e.target.value)} placeholder="Email"
                data-testid={`intake-contact-email-${i}`} className={inputCls} />
              <input value={c.phone} onChange={(e) => setContact(i, "phone", e.target.value)} placeholder="Phone"
                data-testid={`intake-contact-phone-${i}`} className={inputCls} />
              {contacts.length > 1 && (
                <button type="button" onClick={() => setContacts((cs) => cs.filter((_, j) => j !== i))}
                  data-testid={`intake-contact-del-${i}`} title="Remove this person"
                  className="absolute -top-2 -right-2 h-7 w-7 grid place-items-center rounded-full bg-[#121820] border border-red-200 text-[#D5281B] hover:bg-red-50">
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
          <button type="button" onClick={() => setContacts((cs) => [...cs, { ...EMPTY_CONTACT }])} data-testid="intake-contact-add"
            className="h-10 px-4 rounded-lg border-2 border-dashed border-slate-600 text-slate-400 hover:border-[#1e7d8c] hover:text-[#00D2C2] text-xs font-bold inline-flex items-center gap-1.5 transition-colors">
            <Plus className="h-3.5 w-3.5" /> Add another person
          </button>
        </section>

        <section className="bg-[#121820] rounded-2xl border border-slate-700 p-5 space-y-3" data-testid="intake-event">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-bold text-white flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[#00D2C2]" /> An Event You'd Like Covered</h2>
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-300 cursor-pointer">
              <input type="checkbox" checked={wantCover} onChange={(e) => setWantCover(e.target.checked)}
                data-testid="intake-want-cover" className="h-4 w-4 accent-[#1e7d8c]" />
              Yes, quote me
            </label>
          </div>
          <p className="text-xs text-slate-400">Just sending your contact details? Leave this unticked. If you have an event we haven't quoted for yet, tick <strong>Yes, quote me</strong> and we'll put a quote together for you.</p>
          {wantCover && (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className={labelCls}>Date of event *</span>
                  <input type="date" required={wantCover} value={ev.date_of_service} onChange={setEvField("date_of_service")}
                    data-testid="intake-event-date" className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>Event location *</span>
                  <input required={wantCover} value={ev.service_location} onChange={setEvField("service_location")}
                    placeholder="Venue / address" data-testid="intake-event-location" className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>Start time</span>
                  <input type="time" value={ev.start_time} onChange={setEvField("start_time")} data-testid="intake-start" className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>Finish time</span>
                  <input type="time" value={ev.end_time} onChange={setEvField("end_time")} data-testid="intake-end" className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>Number of participants</span>
                  <input type="number" min="0" value={ev.participants} onChange={setEvField("participants")} data-testid="intake-participants" className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>Maximum public capacity</span>
                  <input type="number" min="0" value={ev.max_capacity} onChange={setEvField("max_capacity")} data-testid="intake-capacity" className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>Number of sites covered</span>
                  <input type="number" min="0" value={ev.sites} onChange={setEvField("sites")} data-testid="intake-sites" className={inputCls} />
                </label>
                {yesNo("overnight", "Overnight cover needed?", "intake-overnight")}
                {yesNo("alcohol", "Alcohol present?", "intake-alcohol")}
                {yesNo("security", "Security present?", "intake-security")}
                {yesNo("parking", "Parking available?", "intake-parking")}
                {yesNo("freshwater", "Fresh water available?", "intake-freshwater")}
                {yesNo("power", "Power supplied?", "intake-power")}
              </div>
              <label className="block">
                <span className={labelCls}>Additional information (previous medical resources, anything else)</span>
                <textarea rows={3} value={ev.additional_info} onChange={setEvField("additional_info")} data-testid="intake-additional"
                  className="w-full rounded-lg border border-slate-600 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00D2C2]" />
              </label>
            </>
          )}
        </section>

        <button type="submit" disabled={sending} data-testid="intake-submit"
          className="w-full h-12 rounded-xl bg-[#1e7d8c] hover:bg-[#176472] disabled:opacity-50 text-white text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors">
          <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send to Wales Event Medical Services"}
        </button>
      </form>
      <SiteFooter />
    </div>
  );
}
